import { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { toast } from 'sonner';
import { Gift, CheckCircle, Phone, Mail, User, ChevronDown, RefreshCw, Share2, Copy, Facebook, Twitter } from 'lucide-react';
import { api } from '@/utils/apiClient';

// Country codes with phone number length validation
// Format: { code, name, dialCode, minLength, maxLength }
const COUNTRY_CODES = [
  // Priority countries at top
  { code: 'FJ', name: 'Fiji', dialCode: '+679', minLength: 7, maxLength: 7 },
  { code: 'NZ', name: 'New Zealand', dialCode: '+64', minLength: 9, maxLength: 10 },
  { code: 'AU', name: 'Australia', dialCode: '+61', minLength: 9, maxLength: 9 },
  { code: 'US', name: 'United States', dialCode: '+1', minLength: 10, maxLength: 10 },
  { code: 'CN', name: 'China', dialCode: '+86', minLength: 11, maxLength: 11 },
  // Divider indicator
  { code: 'DIVIDER', name: '──────────────', dialCode: '', minLength: 0, maxLength: 0 },
  // Rest of world alphabetically
  { code: 'AF', name: 'Afghanistan', dialCode: '+93', minLength: 9, maxLength: 9 },
  { code: 'AL', name: 'Albania', dialCode: '+355', minLength: 9, maxLength: 9 },
  { code: 'DZ', name: 'Algeria', dialCode: '+213', minLength: 9, maxLength: 9 },
  { code: 'AD', name: 'Andorra', dialCode: '+376', minLength: 6, maxLength: 9 },
  { code: 'AO', name: 'Angola', dialCode: '+244', minLength: 9, maxLength: 9 },
  { code: 'AR', name: 'Argentina', dialCode: '+54', minLength: 10, maxLength: 11 },
  { code: 'AM', name: 'Armenia', dialCode: '+374', minLength: 8, maxLength: 8 },
  { code: 'AT', name: 'Austria', dialCode: '+43', minLength: 10, maxLength: 13 },
  { code: 'AZ', name: 'Azerbaijan', dialCode: '+994', minLength: 9, maxLength: 9 },
  { code: 'BH', name: 'Bahrain', dialCode: '+973', minLength: 8, maxLength: 8 },
  { code: 'BD', name: 'Bangladesh', dialCode: '+880', minLength: 10, maxLength: 10 },
  { code: 'BY', name: 'Belarus', dialCode: '+375', minLength: 9, maxLength: 9 },
  { code: 'BE', name: 'Belgium', dialCode: '+32', minLength: 9, maxLength: 9 },
  { code: 'BZ', name: 'Belize', dialCode: '+501', minLength: 7, maxLength: 7 },
  { code: 'BJ', name: 'Benin', dialCode: '+229', minLength: 8, maxLength: 8 },
  { code: 'BT', name: 'Bhutan', dialCode: '+975', minLength: 8, maxLength: 8 },
  { code: 'BO', name: 'Bolivia', dialCode: '+591', minLength: 8, maxLength: 8 },
  { code: 'BA', name: 'Bosnia and Herzegovina', dialCode: '+387', minLength: 8, maxLength: 9 },
  { code: 'BW', name: 'Botswana', dialCode: '+267', minLength: 7, maxLength: 8 },
  { code: 'BR', name: 'Brazil', dialCode: '+55', minLength: 10, maxLength: 11 },
  { code: 'BN', name: 'Brunei', dialCode: '+673', minLength: 7, maxLength: 7 },
  { code: 'BG', name: 'Bulgaria', dialCode: '+359', minLength: 9, maxLength: 9 },
  { code: 'BF', name: 'Burkina Faso', dialCode: '+226', minLength: 8, maxLength: 8 },
  { code: 'BI', name: 'Burundi', dialCode: '+257', minLength: 8, maxLength: 8 },
  { code: 'KH', name: 'Cambodia', dialCode: '+855', minLength: 8, maxLength: 9 },
  { code: 'CM', name: 'Cameroon', dialCode: '+237', minLength: 9, maxLength: 9 },
  { code: 'CA', name: 'Canada', dialCode: '+1', minLength: 10, maxLength: 10 },
  { code: 'CV', name: 'Cape Verde', dialCode: '+238', minLength: 7, maxLength: 7 },
  { code: 'CF', name: 'Central African Republic', dialCode: '+236', minLength: 8, maxLength: 8 },
  { code: 'TD', name: 'Chad', dialCode: '+235', minLength: 8, maxLength: 8 },
  { code: 'CL', name: 'Chile', dialCode: '+56', minLength: 9, maxLength: 9 },
  { code: 'CO', name: 'Colombia', dialCode: '+57', minLength: 10, maxLength: 10 },
  { code: 'KM', name: 'Comoros', dialCode: '+269', minLength: 7, maxLength: 7 },
  { code: 'CG', name: 'Congo', dialCode: '+242', minLength: 9, maxLength: 9 },
  { code: 'CD', name: 'Congo (DRC)', dialCode: '+243', minLength: 9, maxLength: 9 },
  { code: 'CR', name: 'Costa Rica', dialCode: '+506', minLength: 8, maxLength: 8 },
  { code: 'HR', name: 'Croatia', dialCode: '+385', minLength: 9, maxLength: 9 },
  { code: 'CU', name: 'Cuba', dialCode: '+53', minLength: 8, maxLength: 8 },
  { code: 'CY', name: 'Cyprus', dialCode: '+357', minLength: 8, maxLength: 8 },
  { code: 'CZ', name: 'Czech Republic', dialCode: '+420', minLength: 9, maxLength: 9 },
  { code: 'DK', name: 'Denmark', dialCode: '+45', minLength: 8, maxLength: 8 },
  { code: 'DJ', name: 'Djibouti', dialCode: '+253', minLength: 8, maxLength: 8 },
  { code: 'DO', name: 'Dominican Republic', dialCode: '+1', minLength: 10, maxLength: 10 },
  { code: 'EC', name: 'Ecuador', dialCode: '+593', minLength: 9, maxLength: 9 },
  { code: 'EG', name: 'Egypt', dialCode: '+20', minLength: 10, maxLength: 10 },
  { code: 'SV', name: 'El Salvador', dialCode: '+503', minLength: 8, maxLength: 8 },
  { code: 'GQ', name: 'Equatorial Guinea', dialCode: '+240', minLength: 9, maxLength: 9 },
  { code: 'ER', name: 'Eritrea', dialCode: '+291', minLength: 7, maxLength: 7 },
  { code: 'EE', name: 'Estonia', dialCode: '+372', minLength: 7, maxLength: 8 },
  { code: 'ET', name: 'Ethiopia', dialCode: '+251', minLength: 9, maxLength: 9 },
  { code: 'FI', name: 'Finland', dialCode: '+358', minLength: 9, maxLength: 10 },
  { code: 'FR', name: 'France', dialCode: '+33', minLength: 9, maxLength: 9 },
  { code: 'GA', name: 'Gabon', dialCode: '+241', minLength: 7, maxLength: 8 },
  { code: 'GM', name: 'Gambia', dialCode: '+220', minLength: 7, maxLength: 7 },
  { code: 'GE', name: 'Georgia', dialCode: '+995', minLength: 9, maxLength: 9 },
  { code: 'DE', name: 'Germany', dialCode: '+49', minLength: 10, maxLength: 11 },
  { code: 'GH', name: 'Ghana', dialCode: '+233', minLength: 9, maxLength: 9 },
  { code: 'GR', name: 'Greece', dialCode: '+30', minLength: 10, maxLength: 10 },
  { code: 'GT', name: 'Guatemala', dialCode: '+502', minLength: 8, maxLength: 8 },
  { code: 'GN', name: 'Guinea', dialCode: '+224', minLength: 9, maxLength: 9 },
  { code: 'GW', name: 'Guinea-Bissau', dialCode: '+245', minLength: 7, maxLength: 7 },
  { code: 'GY', name: 'Guyana', dialCode: '+592', minLength: 7, maxLength: 7 },
  { code: 'HT', name: 'Haiti', dialCode: '+509', minLength: 8, maxLength: 8 },
  { code: 'HN', name: 'Honduras', dialCode: '+504', minLength: 8, maxLength: 8 },
  { code: 'HK', name: 'Hong Kong', dialCode: '+852', minLength: 8, maxLength: 8 },
  { code: 'HU', name: 'Hungary', dialCode: '+36', minLength: 9, maxLength: 9 },
  { code: 'IS', name: 'Iceland', dialCode: '+354', minLength: 7, maxLength: 7 },
  { code: 'IN', name: 'India', dialCode: '+91', minLength: 10, maxLength: 10 },
  { code: 'ID', name: 'Indonesia', dialCode: '+62', minLength: 9, maxLength: 12 },
  { code: 'IR', name: 'Iran', dialCode: '+98', minLength: 10, maxLength: 10 },
  { code: 'IQ', name: 'Iraq', dialCode: '+964', minLength: 10, maxLength: 10 },
  { code: 'IE', name: 'Ireland', dialCode: '+353', minLength: 9, maxLength: 9 },
  { code: 'IL', name: 'Israel', dialCode: '+972', minLength: 9, maxLength: 9 },
  { code: 'IT', name: 'Italy', dialCode: '+39', minLength: 9, maxLength: 10 },
  { code: 'CI', name: 'Ivory Coast', dialCode: '+225', minLength: 10, maxLength: 10 },
  { code: 'JM', name: 'Jamaica', dialCode: '+1', minLength: 10, maxLength: 10 },
  { code: 'JP', name: 'Japan', dialCode: '+81', minLength: 10, maxLength: 10 },
  { code: 'JO', name: 'Jordan', dialCode: '+962', minLength: 9, maxLength: 9 },
  { code: 'KZ', name: 'Kazakhstan', dialCode: '+7', minLength: 10, maxLength: 10 },
  { code: 'KE', name: 'Kenya', dialCode: '+254', minLength: 9, maxLength: 9 },
  { code: 'KI', name: 'Kiribati', dialCode: '+686', minLength: 5, maxLength: 8 },
  { code: 'KW', name: 'Kuwait', dialCode: '+965', minLength: 8, maxLength: 8 },
  { code: 'KG', name: 'Kyrgyzstan', dialCode: '+996', minLength: 9, maxLength: 9 },
  { code: 'LA', name: 'Laos', dialCode: '+856', minLength: 8, maxLength: 10 },
  { code: 'LV', name: 'Latvia', dialCode: '+371', minLength: 8, maxLength: 8 },
  { code: 'LB', name: 'Lebanon', dialCode: '+961', minLength: 7, maxLength: 8 },
  { code: 'LS', name: 'Lesotho', dialCode: '+266', minLength: 8, maxLength: 8 },
  { code: 'LR', name: 'Liberia', dialCode: '+231', minLength: 7, maxLength: 9 },
  { code: 'LY', name: 'Libya', dialCode: '+218', minLength: 9, maxLength: 9 },
  { code: 'LI', name: 'Liechtenstein', dialCode: '+423', minLength: 7, maxLength: 9 },
  { code: 'LT', name: 'Lithuania', dialCode: '+370', minLength: 8, maxLength: 8 },
  { code: 'LU', name: 'Luxembourg', dialCode: '+352', minLength: 9, maxLength: 9 },
  { code: 'MO', name: 'Macau', dialCode: '+853', minLength: 8, maxLength: 8 },
  { code: 'MK', name: 'North Macedonia', dialCode: '+389', minLength: 8, maxLength: 8 },
  { code: 'MG', name: 'Madagascar', dialCode: '+261', minLength: 9, maxLength: 9 },
  { code: 'MW', name: 'Malawi', dialCode: '+265', minLength: 7, maxLength: 9 },
  { code: 'MY', name: 'Malaysia', dialCode: '+60', minLength: 9, maxLength: 10 },
  { code: 'MV', name: 'Maldives', dialCode: '+960', minLength: 7, maxLength: 7 },
  { code: 'ML', name: 'Mali', dialCode: '+223', minLength: 8, maxLength: 8 },
  { code: 'MT', name: 'Malta', dialCode: '+356', minLength: 8, maxLength: 8 },
  { code: 'MH', name: 'Marshall Islands', dialCode: '+692', minLength: 7, maxLength: 7 },
  { code: 'MR', name: 'Mauritania', dialCode: '+222', minLength: 8, maxLength: 8 },
  { code: 'MU', name: 'Mauritius', dialCode: '+230', minLength: 7, maxLength: 8 },
  { code: 'MX', name: 'Mexico', dialCode: '+52', minLength: 10, maxLength: 10 },
  { code: 'FM', name: 'Micronesia', dialCode: '+691', minLength: 7, maxLength: 7 },
  { code: 'MD', name: 'Moldova', dialCode: '+373', minLength: 8, maxLength: 8 },
  { code: 'MC', name: 'Monaco', dialCode: '+377', minLength: 8, maxLength: 9 },
  { code: 'MN', name: 'Mongolia', dialCode: '+976', minLength: 8, maxLength: 8 },
  { code: 'ME', name: 'Montenegro', dialCode: '+382', minLength: 8, maxLength: 8 },
  { code: 'MA', name: 'Morocco', dialCode: '+212', minLength: 9, maxLength: 9 },
  { code: 'MZ', name: 'Mozambique', dialCode: '+258', minLength: 9, maxLength: 9 },
  { code: 'MM', name: 'Myanmar', dialCode: '+95', minLength: 8, maxLength: 10 },
  { code: 'NA', name: 'Namibia', dialCode: '+264', minLength: 9, maxLength: 9 },
  { code: 'NR', name: 'Nauru', dialCode: '+674', minLength: 7, maxLength: 7 },
  { code: 'NP', name: 'Nepal', dialCode: '+977', minLength: 10, maxLength: 10 },
  { code: 'NL', name: 'Netherlands', dialCode: '+31', minLength: 9, maxLength: 9 },
  { code: 'NC', name: 'New Caledonia', dialCode: '+687', minLength: 6, maxLength: 6 },
  { code: 'NI', name: 'Nicaragua', dialCode: '+505', minLength: 8, maxLength: 8 },
  { code: 'NE', name: 'Niger', dialCode: '+227', minLength: 8, maxLength: 8 },
  { code: 'NG', name: 'Nigeria', dialCode: '+234', minLength: 10, maxLength: 10 },
  { code: 'KP', name: 'North Korea', dialCode: '+850', minLength: 8, maxLength: 9 },
  { code: 'NO', name: 'Norway', dialCode: '+47', minLength: 8, maxLength: 8 },
  { code: 'OM', name: 'Oman', dialCode: '+968', minLength: 8, maxLength: 8 },
  { code: 'PK', name: 'Pakistan', dialCode: '+92', minLength: 10, maxLength: 10 },
  { code: 'PW', name: 'Palau', dialCode: '+680', minLength: 7, maxLength: 7 },
  { code: 'PS', name: 'Palestine', dialCode: '+970', minLength: 9, maxLength: 9 },
  { code: 'PA', name: 'Panama', dialCode: '+507', minLength: 8, maxLength: 8 },
  { code: 'PG', name: 'Papua New Guinea', dialCode: '+675', minLength: 8, maxLength: 8 },
  { code: 'PY', name: 'Paraguay', dialCode: '+595', minLength: 9, maxLength: 9 },
  { code: 'PE', name: 'Peru', dialCode: '+51', minLength: 9, maxLength: 9 },
  { code: 'PH', name: 'Philippines', dialCode: '+63', minLength: 10, maxLength: 10 },
  { code: 'PL', name: 'Poland', dialCode: '+48', minLength: 9, maxLength: 9 },
  { code: 'PT', name: 'Portugal', dialCode: '+351', minLength: 9, maxLength: 9 },
  { code: 'PR', name: 'Puerto Rico', dialCode: '+1', minLength: 10, maxLength: 10 },
  { code: 'QA', name: 'Qatar', dialCode: '+974', minLength: 8, maxLength: 8 },
  { code: 'RO', name: 'Romania', dialCode: '+40', minLength: 9, maxLength: 9 },
  { code: 'RU', name: 'Russia', dialCode: '+7', minLength: 10, maxLength: 10 },
  { code: 'RW', name: 'Rwanda', dialCode: '+250', minLength: 9, maxLength: 9 },
  { code: 'WS', name: 'Samoa', dialCode: '+685', minLength: 5, maxLength: 7 },
  { code: 'SM', name: 'San Marino', dialCode: '+378', minLength: 6, maxLength: 10 },
  { code: 'ST', name: 'Sao Tome and Principe', dialCode: '+239', minLength: 7, maxLength: 7 },
  { code: 'SA', name: 'Saudi Arabia', dialCode: '+966', minLength: 9, maxLength: 9 },
  { code: 'SN', name: 'Senegal', dialCode: '+221', minLength: 9, maxLength: 9 },
  { code: 'RS', name: 'Serbia', dialCode: '+381', minLength: 9, maxLength: 9 },
  { code: 'SC', name: 'Seychelles', dialCode: '+248', minLength: 7, maxLength: 7 },
  { code: 'SL', name: 'Sierra Leone', dialCode: '+232', minLength: 8, maxLength: 8 },
  { code: 'SG', name: 'Singapore', dialCode: '+65', minLength: 8, maxLength: 8 },
  { code: 'SK', name: 'Slovakia', dialCode: '+421', minLength: 9, maxLength: 9 },
  { code: 'SI', name: 'Slovenia', dialCode: '+386', minLength: 8, maxLength: 8 },
  { code: 'SB', name: 'Solomon Islands', dialCode: '+677', minLength: 5, maxLength: 7 },
  { code: 'SO', name: 'Somalia', dialCode: '+252', minLength: 8, maxLength: 9 },
  { code: 'ZA', name: 'South Africa', dialCode: '+27', minLength: 9, maxLength: 9 },
  { code: 'KR', name: 'South Korea', dialCode: '+82', minLength: 9, maxLength: 10 },
  { code: 'SS', name: 'South Sudan', dialCode: '+211', minLength: 9, maxLength: 9 },
  { code: 'ES', name: 'Spain', dialCode: '+34', minLength: 9, maxLength: 9 },
  { code: 'LK', name: 'Sri Lanka', dialCode: '+94', minLength: 9, maxLength: 9 },
  { code: 'SD', name: 'Sudan', dialCode: '+249', minLength: 9, maxLength: 9 },
  { code: 'SR', name: 'Suriname', dialCode: '+597', minLength: 6, maxLength: 7 },
  { code: 'SZ', name: 'Eswatini', dialCode: '+268', minLength: 8, maxLength: 8 },
  { code: 'SE', name: 'Sweden', dialCode: '+46', minLength: 9, maxLength: 9 },
  { code: 'CH', name: 'Switzerland', dialCode: '+41', minLength: 9, maxLength: 9 },
  { code: 'SY', name: 'Syria', dialCode: '+963', minLength: 9, maxLength: 9 },
  { code: 'TW', name: 'Taiwan', dialCode: '+886', minLength: 9, maxLength: 9 },
  { code: 'TJ', name: 'Tajikistan', dialCode: '+992', minLength: 9, maxLength: 9 },
  { code: 'TZ', name: 'Tanzania', dialCode: '+255', minLength: 9, maxLength: 9 },
  { code: 'TH', name: 'Thailand', dialCode: '+66', minLength: 9, maxLength: 9 },
  { code: 'TL', name: 'Timor-Leste', dialCode: '+670', minLength: 7, maxLength: 8 },
  { code: 'TG', name: 'Togo', dialCode: '+228', minLength: 8, maxLength: 8 },
  { code: 'TO', name: 'Tonga', dialCode: '+676', minLength: 5, maxLength: 7 },
  { code: 'TT', name: 'Trinidad and Tobago', dialCode: '+1', minLength: 10, maxLength: 10 },
  { code: 'TN', name: 'Tunisia', dialCode: '+216', minLength: 8, maxLength: 8 },
  { code: 'TR', name: 'Turkey', dialCode: '+90', minLength: 10, maxLength: 10 },
  { code: 'TM', name: 'Turkmenistan', dialCode: '+993', minLength: 8, maxLength: 8 },
  { code: 'TV', name: 'Tuvalu', dialCode: '+688', minLength: 5, maxLength: 6 },
  { code: 'UG', name: 'Uganda', dialCode: '+256', minLength: 9, maxLength: 9 },
  { code: 'UA', name: 'Ukraine', dialCode: '+380', minLength: 9, maxLength: 9 },
  { code: 'AE', name: 'United Arab Emirates', dialCode: '+971', minLength: 9, maxLength: 9 },
  { code: 'GB', name: 'United Kingdom', dialCode: '+44', minLength: 10, maxLength: 10 },
  { code: 'UY', name: 'Uruguay', dialCode: '+598', minLength: 8, maxLength: 8 },
  { code: 'UZ', name: 'Uzbekistan', dialCode: '+998', minLength: 9, maxLength: 9 },
  { code: 'VU', name: 'Vanuatu', dialCode: '+678', minLength: 5, maxLength: 7 },
  { code: 'VA', name: 'Vatican City', dialCode: '+379', minLength: 6, maxLength: 10 },
  { code: 'VE', name: 'Venezuela', dialCode: '+58', minLength: 10, maxLength: 10 },
  { code: 'VN', name: 'Vietnam', dialCode: '+84', minLength: 9, maxLength: 10 },
  { code: 'YE', name: 'Yemen', dialCode: '+967', minLength: 9, maxLength: 9 },
  { code: 'ZM', name: 'Zambia', dialCode: '+260', minLength: 9, maxLength: 9 },
  { code: 'ZW', name: 'Zimbabwe', dialCode: '+263', minLength: 9, maxLength: 9 },
];

// Campaign themes with good text contrast for both light and dark modes
const campaignThemes = {
  1: {
    gradient: 'from-teal-100 to-emerald-200 dark:from-teal-800 dark:to-emerald-900',
    accent: 'bg-teal-500',
    buttonBg: 'bg-teal-600 hover:bg-teal-700',
    textColor: 'text-teal-900 dark:text-white',
    subtextColor: 'text-teal-700 dark:text-teal-200',
    footerColor: 'text-teal-600 dark:text-teal-300',
    bgPattern: 'radial-gradient(circle at 20% 80%, rgba(20, 184, 166, 0.2) 0%, transparent 50%)'
  },
  2: {
    gradient: 'from-amber-100 to-orange-200 dark:from-amber-800 dark:to-orange-900',
    accent: 'bg-amber-500',
    buttonBg: 'bg-amber-600 hover:bg-amber-700',
    textColor: 'text-amber-900 dark:text-white',
    subtextColor: 'text-amber-700 dark:text-amber-200',
    footerColor: 'text-amber-600 dark:text-amber-300',
    bgPattern: 'radial-gradient(circle at 80% 20%, rgba(217, 119, 6, 0.2) 0%, transparent 50%)'
  },
  3: {
    gradient: 'from-violet-100 to-purple-200 dark:from-violet-800 dark:to-purple-900',
    accent: 'bg-violet-500',
    buttonBg: 'bg-violet-600 hover:bg-violet-700',
    textColor: 'text-violet-900 dark:text-white',
    subtextColor: 'text-violet-700 dark:text-violet-200',
    footerColor: 'text-violet-600 dark:text-violet-300',
    bgPattern: 'radial-gradient(circle at 50% 50%, rgba(124, 58, 237, 0.2) 0%, transparent 50%)'
  },
  4: {
    gradient: 'from-gray-200 to-gray-300 dark:from-gray-800 dark:to-gray-900',
    accent: 'bg-gray-800',
    buttonBg: 'bg-gray-800 hover:bg-gray-900',
    textColor: 'text-gray-900 dark:text-white',
    subtextColor: 'text-gray-700 dark:text-gray-200',
    footerColor: 'text-gray-600 dark:text-gray-300',
    bgPattern: 'radial-gradient(circle at 30% 70%, rgba(26, 26, 26, 0.15) 0%, transparent 50%)'
  },
  // Dynamic themes for new campaigns
  teal: {
    gradient: 'from-teal-100 to-emerald-200 dark:from-teal-800 dark:to-emerald-900',
    accent: 'bg-teal-500',
    buttonBg: 'bg-teal-600 hover:bg-teal-700',
    textColor: 'text-teal-900 dark:text-white',
    subtextColor: 'text-teal-700 dark:text-teal-200',
    footerColor: 'text-teal-600 dark:text-teal-300',
    bgPattern: 'radial-gradient(circle at 20% 80%, rgba(20, 184, 166, 0.2) 0%, transparent 50%)'
  },
  orange: {
    gradient: 'from-amber-100 to-orange-200 dark:from-amber-800 dark:to-orange-900',
    accent: 'bg-amber-500',
    buttonBg: 'bg-amber-600 hover:bg-amber-700',
    textColor: 'text-amber-900 dark:text-white',
    subtextColor: 'text-amber-700 dark:text-amber-200',
    footerColor: 'text-amber-600 dark:text-amber-300',
    bgPattern: 'radial-gradient(circle at 80% 20%, rgba(217, 119, 6, 0.2) 0%, transparent 50%)'
  },
  purple: {
    gradient: 'from-violet-100 to-purple-200 dark:from-violet-800 dark:to-purple-900',
    accent: 'bg-violet-500',
    buttonBg: 'bg-violet-600 hover:bg-violet-700',
    textColor: 'text-violet-900 dark:text-white',
    subtextColor: 'text-violet-700 dark:text-violet-200',
    footerColor: 'text-violet-600 dark:text-violet-300',
    bgPattern: 'radial-gradient(circle at 50% 50%, rgba(124, 58, 237, 0.2) 0%, transparent 50%)'
  },
  black: {
    gradient: 'from-gray-200 to-gray-300 dark:from-gray-800 dark:to-gray-900',
    accent: 'bg-gray-800',
    buttonBg: 'bg-gray-800 hover:bg-gray-900',
    textColor: 'text-gray-900 dark:text-white',
    subtextColor: 'text-gray-700 dark:text-gray-200',
    footerColor: 'text-gray-600 dark:text-gray-300',
    bgPattern: 'radial-gradient(circle at 30% 70%, rgba(26, 26, 26, 0.15) 0%, transparent 50%)'
  },
  blue: {
    gradient: 'from-blue-100 to-blue-200 dark:from-blue-800 dark:to-blue-900',
    accent: 'bg-blue-500',
    buttonBg: 'bg-blue-600 hover:bg-blue-700',
    textColor: 'text-blue-900 dark:text-white',
    subtextColor: 'text-blue-700 dark:text-blue-200',
    footerColor: 'text-blue-600 dark:text-blue-300',
    bgPattern: 'radial-gradient(circle at 40% 60%, rgba(37, 99, 235, 0.2) 0%, transparent 50%)'
  },
  green: {
    gradient: 'from-green-100 to-green-200 dark:from-green-800 dark:to-green-900',
    accent: 'bg-green-500',
    buttonBg: 'bg-green-600 hover:bg-green-700',
    textColor: 'text-green-900 dark:text-white',
    subtextColor: 'text-green-700 dark:text-green-200',
    footerColor: 'text-green-600 dark:text-green-300',
    bgPattern: 'radial-gradient(circle at 60% 40%, rgba(22, 163, 74, 0.2) 0%, transparent 50%)'
  },
  red: {
    gradient: 'from-red-100 to-red-200 dark:from-red-800 dark:to-red-900',
    accent: 'bg-red-500',
    buttonBg: 'bg-red-600 hover:bg-red-700',
    textColor: 'text-red-900 dark:text-white',
    subtextColor: 'text-red-700 dark:text-red-200',
    footerColor: 'text-red-600 dark:text-red-300',
    bgPattern: 'radial-gradient(circle at 70% 30%, rgba(220, 38, 38, 0.2) 0%, transparent 50%)'
  },
  pink: {
    gradient: 'from-pink-100 to-pink-200 dark:from-pink-800 dark:to-pink-900',
    accent: 'bg-pink-500',
    buttonBg: 'bg-pink-600 hover:bg-pink-700',
    textColor: 'text-pink-900 dark:text-white',
    subtextColor: 'text-pink-700 dark:text-pink-200',
    footerColor: 'text-pink-600 dark:text-pink-300',
    bgPattern: 'radial-gradient(circle at 25% 75%, rgba(219, 39, 119, 0.2) 0%, transparent 50%)'
  },
  indigo: {
    gradient: 'from-indigo-100 to-indigo-200 dark:from-indigo-800 dark:to-indigo-900',
    accent: 'bg-indigo-500',
    buttonBg: 'bg-indigo-600 hover:bg-indigo-700',
    textColor: 'text-indigo-900 dark:text-white',
    subtextColor: 'text-indigo-700 dark:text-indigo-200',
    footerColor: 'text-indigo-600 dark:text-indigo-300',
    bgPattern: 'radial-gradient(circle at 55% 45%, rgba(79, 70, 229, 0.2) 0%, transparent 50%)'
  },
  amber: {
    gradient: 'from-amber-100 to-amber-200 dark:from-amber-800 dark:to-amber-900',
    accent: 'bg-amber-500',
    buttonBg: 'bg-amber-600 hover:bg-amber-700',
    textColor: 'text-amber-900 dark:text-white',
    subtextColor: 'text-amber-700 dark:text-amber-200',
    footerColor: 'text-amber-600 dark:text-amber-300',
    bgPattern: 'radial-gradient(circle at 35% 65%, rgba(245, 158, 11, 0.2) 0%, transparent 50%)'
  }
};

// Logo URL - can be overridden via environment variable for production
const LOGO_URL = process.env.REACT_APP_LOGO_URL || "/images/bulaccino-logo-white.png";

export default function CampaignPage() {
  const { campaignId } = useParams();
  const navigate = useNavigate();
  const [campaign, setCampaign] = useState(null);
  const [campaignStatus, setCampaignStatus] = useState(null);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [success, setSuccess] = useState(false);
  const [couponData, setCouponData] = useState(null);
  const [errors, setErrors] = useState({});
  const [selectedCountry, setSelectedCountry] = useState(COUNTRY_CODES[0]); // Default to Fiji
  const [emailCountdown, setEmailCountdown] = useState(0);
  const [smsCountdown, setSmsCountdown] = useState(0);
  const [resendingEmail, setResendingEmail] = useState(false);
  const [resendingSms, setResendingSms] = useState(false);
  
  const [formData, setFormData] = useState({
    name: '',
    phone: '',
    email: ''
  });

  const campaignNum = parseInt(campaignId);
  
  // Get theme - first try campaign number (for legacy), then use theme name from API response
  const getTheme = () => {
    // Legacy campaigns 1-4 use numeric theme
    if (campaignThemes[campaignNum]) {
      return campaignThemes[campaignNum];
    }
    // New campaigns use theme name from API
    if (campaign?.theme && campaignThemes[campaign.theme]) {
      return campaignThemes[campaign.theme];
    }
    // Default fallback
    return campaignThemes.teal;
  };
  
  const theme = getTheme();

  // Countdown timer for resend buttons
  useEffect(() => {
    if (emailCountdown > 0) {
      const timer = setTimeout(() => setEmailCountdown(emailCountdown - 1), 1000);
      return () => clearTimeout(timer);
    }
  }, [emailCountdown]);

  useEffect(() => {
    if (smsCountdown > 0) {
      const timer = setTimeout(() => setSmsCountdown(smsCountdown - 1), 1000);
      return () => clearTimeout(timer);
    }
  }, [smsCountdown]);

  // Start countdown when signup is successful
  useEffect(() => {
    if (success && couponData) {
      // If email/sms was sent, start the 59 second countdown
      if (couponData.email_sent) {
        setEmailCountdown(59);
      }
      if (couponData.sms_sent) {
        setSmsCountdown(59);
      }
    }
  }, [success, couponData]);

  const handleResendEmail = async () => {
    if (!couponData?.signup_id || emailCountdown > 0) return;
    
    setResendingEmail(true);
    try {
      const res = await api.post(`/api/campaigns/resend/${couponData.signup_id}?notification_type=email`);
      
      if (res.data.email_sent) {
        toast.success('Email resent successfully!');
        setEmailCountdown(59);
      } else if (res.data.errors?.length) {
        toast.error(res.data.errors[0]);
      }
    } catch (err) {
      toast.error('Failed to resend email');
    }
    setResendingEmail(false);
  };

  const handleResendSms = async () => {
    if (!couponData?.signup_id || smsCountdown > 0) return;
    
    setResendingSms(true);
    try {
      const res = await api.post(`/api/campaigns/resend/${couponData.signup_id}?notification_type=sms`);
      
      if (res.data.sms_sent) {
        toast.success('SMS resent successfully!');
        setSmsCountdown(59);
      } else if (res.data.errors?.length) {
        toast.error(res.data.errors[0]);
      }
    } catch (err) {
      toast.error('Failed to resend SMS');
    }
    setResendingSms(false);
  };

  useEffect(() => {
    if (!campaignId || isNaN(campaignNum) || campaignNum < 1) {
      navigate('/');
      return;
    }

    // Track campaign view for analytics
    api.post(`/api/campaigns/${campaignId}/track-view`)
      .catch(() => {}); // Silently fail - analytics shouldn't block the page

    // Fetch campaign status (founding member status) and campaign info in parallel
    Promise.allSettled([
      api.get('/api/founding-members/campaign-status'),
      api.get(`/api/campaigns/${campaignId}`)
    ])
      .then(([statusResult, campaignResult]) => {
        const statusData = statusResult.status === 'fulfilled' ? statusResult.value.data : null;
        const campaignData = campaignResult.status === 'fulfilled' ? campaignResult.value.data : null;
        
        // If campaign doesn't exist (API returns error), redirect to home
        if (campaignData?.detail === "Campaign not found") {
          navigate('/');
          return;
        }
        setCampaignStatus(statusData);
        setCampaign(campaignData);
        setLoading(false);
      })
      .catch(err => {
        console.error(err);
        navigate('/');
      });
  }, [campaignId, campaignNum, navigate]);

  const validatePhone = (phoneDigits) => {
    const { minLength, maxLength, name } = selectedCountry;
    const len = phoneDigits.length;
    
    if (len < minLength) {
      return `Phone number for ${name} requires at least ${minLength} digits`;
    }
    if (len > maxLength) {
      return `Phone number for ${name} should not exceed ${maxLength} digits`;
    }
    return null;
  };

  const validateForm = () => {
    const newErrors = {};
    
    if (!formData.name.trim()) {
      newErrors.name = 'Name is required';
    }
    
    if (!selectedCountry || selectedCountry.code === 'DIVIDER') {
      newErrors.countryCode = 'Please select a country code';
    }
    
    if (!formData.phone.trim()) {
      newErrors.phone = 'Phone number is required';
    } else {
      const phoneDigits = formData.phone.replace(/\D/g, '');
      const phoneError = validatePhone(phoneDigits);
      if (phoneError) {
        newErrors.phone = phoneError;
      }
    }
    
    if (!formData.email.trim()) {
      newErrors.email = 'Email is required';
    } else if (!formData.email.includes('@') || !formData.email.includes('.')) {
      newErrors.email = 'Please enter a valid email address';
    }
    
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!validateForm()) {
      toast.error('Please fill in all required fields correctly');
      return;
    }

    setSubmitting(true);

    // Format phone number - keep country code separate for admin
    const phoneDigits = formData.phone.replace(/\D/g, '');
    const fullPhone = `${selectedCountry.dialCode}${phoneDigits}`;

    try {
      const res = await api.post(`/api/campaigns/${campaignId}/signup`, {
        name: formData.name,
        email: formData.email,
        phone: fullPhone,
        country_code: selectedCountry.dialCode,
        country_name: selectedCountry.name,
        phone_local: phoneDigits
      });

      if (res.data.success) {
        setCouponData(res.data);
        setSuccess(true);
        toast.success(res.data.is_existing ? 'Welcome back!' : 'Registration successful!');
      } else {
        toast.error(res.data.message || 'Something went wrong');
      }
    } catch (err) {
      toast.error('Failed to register. Please try again.');
    }

    setSubmitting(false);
  };

  const handlePhoneChange = (e) => {
    // Only allow digits, spaces, and dashes
    const value = e.target.value.replace(/[^\d\s-]/g, '');
    setFormData(prev => ({ ...prev, phone: value }));
    if (errors.phone) setErrors(prev => ({ ...prev, phone: '' }));
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-900">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-white"></div>
      </div>
    );
  }

  // Success / Thank You Page
  if (success && couponData) {
    const isFoundingMember = couponData.is_founding_member;
    
    return (
      <div className={`min-h-screen bg-gradient-to-br ${theme.gradient} flex items-center justify-center p-6`}>
        <motion.div 
          initial={{ opacity: 0, scale: 0.9 }}
          animate={{ opacity: 1, scale: 1 }}
          className="bg-white dark:bg-gray-800 rounded-3xl shadow-2xl max-w-md w-full p-8 text-center"
        >
          <motion.div
            initial={{ scale: 0 }}
            animate={{ scale: 1 }}
            transition={{ delay: 0.2, type: 'spring' }}
            className={`w-20 h-20 ${theme.accent} rounded-full flex items-center justify-center mx-auto mb-6`}
          >
            <CheckCircle className="w-10 h-10 text-white" />
          </motion.div>

          <h1 className="font-heading text-3xl text-gray-900 dark:text-white mb-2">
            Vinaka
          </h1>
          <h2 className="font-heading text-2xl text-gray-700 dark:text-gray-200 mb-6">
            {isFoundingMember ? 'Welcome, Founding Member!' : 'Welcome to Bulaccino Rewards!'}
          </h2>

          <p className="text-gray-600 dark:text-gray-300 mb-6">
            {isFoundingMember ? (
              <>Your 10% discount on every order for the first 30 days the Namaka Bulaccino Cafe location is open is below. Save this code! Grand Opening is coming soon!</>
            ) : (
              <>You're now part of Bulaccino Rewards! Start earning toward Bronze, Silver, Gold, and Platinum lifetime benefits with every purchase.</>
            )}
          </p>

          {couponData.coupon_code && (
            <div className="bg-gray-100 dark:bg-gray-700 rounded-2xl p-6 mb-6">
              <p className="text-sm text-gray-500 dark:text-gray-400 mb-2">Your Unique Code</p>
              <p className="text-3xl font-bold text-gray-900 dark:text-white font-mono tracking-widest">
                {couponData.coupon_code}
              </p>
            </div>
          )}

          {isFoundingMember && (
            <div className="flex items-center justify-center gap-2 text-sm text-primary mb-4">
              <Gift className="w-4 h-4" />
              <span>This code has been added to your account.</span>
            </div>
          )}
          
          {!isFoundingMember && (
            <div className="bg-amber-50 dark:bg-amber-900/20 rounded-xl p-4 mb-4">
              <p className="text-sm text-amber-800 dark:text-amber-200">
                <strong>Earn Your First Tier:</strong> Spend 1,000 FJD to unlock Bronze status and get 10% off for life!
              </p>
            </div>
          )}

          {/* Resend Buttons Section */}
          <div className="bg-gray-50 dark:bg-gray-700 rounded-xl p-4">
            <p className="text-sm text-gray-600 dark:text-gray-300 mb-3">Didn't receive your confirmation?</p>
            <div className="flex gap-3 justify-center">
              {/* Resend Email Button */}
              <button
                onClick={handleResendEmail}
                disabled={emailCountdown > 0 || resendingEmail}
                className={`flex items-center gap-2 px-4 py-2 rounded-full text-sm font-medium transition-all ${
                  emailCountdown > 0 
                    ? 'bg-gray-200 text-gray-400 cursor-not-allowed' 
                    : 'bg-blue-100 text-blue-700 hover:bg-blue-200'
                }`}
              >
                {resendingEmail ? (
                  <RefreshCw className="w-4 h-4 animate-spin" />
                ) : (
                  <Mail className="w-4 h-4" />
                )}
                {emailCountdown > 0 ? (
                  <span>Resend Email ({emailCountdown}s)</span>
                ) : (
                  <span>Resend Email</span>
                )}
              </button>

              {/* Resend SMS Button */}
              <button
                onClick={handleResendSms}
                disabled={smsCountdown > 0 || resendingSms}
                className={`flex items-center gap-2 px-4 py-2 rounded-full text-sm font-medium transition-all ${
                  smsCountdown > 0 
                    ? 'bg-gray-200 text-gray-400 cursor-not-allowed' 
                    : 'bg-green-100 text-green-700 hover:bg-green-200'
                }`}
              >
                {resendingSms ? (
                  <RefreshCw className="w-4 h-4 animate-spin" />
                ) : (
                  <Phone className="w-4 h-4" />
                )}
                {smsCountdown > 0 ? (
                  <span>Resend SMS ({smsCountdown}s)</span>
                ) : (
                  <span>Resend SMS</span>
                )}
              </button>
            </div>
          </div>

          {/* Share with Friends Section */}
          <div className="mt-6 bg-gradient-to-r from-primary/10 to-amber-500/10 rounded-xl p-4 border border-primary/20">
            <div className="flex items-center gap-2 mb-3 justify-center">
              <Share2 className="w-4 h-4 text-primary" />
              <p className="text-sm font-medium text-gray-700 dark:text-gray-200">Share with friends & earn rewards!</p>
            </div>
            <p className="text-xs text-gray-500 dark:text-gray-400 mb-4 text-center">
              When your friends sign up using your code, you both get extra perks!
            </p>
            <div className="flex gap-2 justify-center flex-wrap">
              {/* Copy Referral Link */}
              <button
                onClick={() => {
                  const referralUrl = `https://bulaccinocafe.com/join/${campaignId}?ref=${couponData.coupon_code}`;
                  navigator.clipboard.writeText(referralUrl);
                  toast.success('Referral link copied!');
                }}
                className="flex items-center gap-2 px-4 py-2 bg-gray-100 dark:bg-gray-600 hover:bg-gray-200 dark:hover:bg-gray-500 rounded-full text-sm font-medium text-gray-700 dark:text-gray-200 transition-all"
              >
                <Copy className="w-4 h-4" />
                Copy Link
              </button>
              
              {/* Share on WhatsApp */}
              <a
                href={`https://wa.me/?text=${encodeURIComponent(`Join me at Bulaccino Cafe and get 10% off! Use my referral: https://bulaccinocafe.com/join/${campaignId}?ref=${couponData.coupon_code}`)}`}
                target="_blank"
                rel="noopener noreferrer"
                className="flex items-center gap-2 px-4 py-2 bg-green-100 hover:bg-green-200 rounded-full text-sm font-medium text-green-700 transition-all"
              >
                <svg className="w-4 h-4" viewBox="0 0 24 24" fill="currentColor">
                  <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"/>
                </svg>
                WhatsApp
              </a>
              
              {/* Share on Facebook */}
              <a
                href={`https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(`https://bulaccinocafe.com/join/${campaignId}?ref=${couponData.coupon_code}`)}`}
                target="_blank"
                rel="noopener noreferrer"
                className="flex items-center gap-2 px-4 py-2 bg-blue-100 hover:bg-blue-200 rounded-full text-sm font-medium text-blue-700 transition-all"
              >
                <svg className="w-4 h-4" viewBox="0 0 24 24" fill="currentColor">
                  <path d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z"/>
                </svg>
                Facebook
              </a>
            </div>
          </div>
        </motion.div>
      </div>
    );
  }

  return (
    <div 
      className={`min-h-screen bg-gradient-to-br ${theme.gradient}`}
      style={{ backgroundImage: theme.bgPattern }}
    >
      {/* Header with Logo */}
      <header className="pt-8 pb-4 px-6">
        <div className="max-w-md mx-auto flex justify-center">
          <div className="dark:bg-white/90 dark:rounded-2xl dark:p-4 dark:shadow-lg transition-all">
            <img 
              src={LOGO_URL}
              alt="Bulaccino Cafe" 
              className="h-32 w-auto"
            />
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="px-6 pb-12">
        <div className="max-w-md mx-auto">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="text-center mb-8"
          >
            <h1 className={`font-heading text-3xl md:text-4xl ${theme.textColor} mb-4 leading-tight`}>
              {campaign?.title}
            </h1>

            <p className={`${theme.subtextColor} text-lg`}>
              {campaign?.description}
            </p>
          </motion.div>

          {/* Registration Form */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.2 }}
            className="bg-white dark:bg-gray-900 rounded-3xl shadow-2xl p-8"
          >
            <form onSubmit={handleSubmit} className="space-y-5">
              <div className="space-y-2">
                <Label className="text-gray-700 dark:text-gray-200 flex items-center gap-2">
                  <User className="w-4 h-4" />
                  Name <span className="text-red-500">*</span>
                </Label>
                <Input
                  type="text"
                  placeholder="Enter your name"
                  value={formData.name}
                  onChange={(e) => {
                    setFormData(prev => ({ ...prev, name: e.target.value }));
                    if (errors.name) setErrors(prev => ({ ...prev, name: '' }));
                  }}
                  className={`rounded-xl border-gray-200 dark:border-gray-700 dark:bg-gray-800 dark:text-white focus:border-gray-400 ${errors.name ? 'border-red-500' : ''}`}
                  data-testid="campaign-name-input"
                  required
                />
                {errors.name && <p className="text-red-500 text-xs">{errors.name}</p>}
              </div>

              {/* Phone Number with Country Code Dropdown */}
              <div className="space-y-3">
                <Label className="text-gray-700 dark:text-gray-200 flex items-center gap-2">
                  <Phone className="w-4 h-4" />
                  Phone Number <span className="text-red-500">*</span>
                </Label>
                
                {/* Country Code Dropdown */}
                <div className="relative">
                  <select
                    value={selectedCountry.code}
                    onChange={(e) => {
                      const country = COUNTRY_CODES.find(c => c.code === e.target.value);
                      if (country && country.code !== 'DIVIDER') {
                        setSelectedCountry(country);
                        if (errors.countryCode) setErrors(prev => ({ ...prev, countryCode: '' }));
                        // Re-validate phone if already entered
                        if (formData.phone) {
                          const phoneDigits = formData.phone.replace(/\D/g, '');
                          const phoneError = validatePhone(phoneDigits);
                          if (phoneError) {
                            setErrors(prev => ({ ...prev, phone: phoneError }));
                          } else {
                            setErrors(prev => ({ ...prev, phone: '' }));
                          }
                        }
                      }
                    }}
                    className={`w-full h-12 pl-4 pr-10 rounded-xl border appearance-none bg-white dark:bg-gray-800 dark:text-white text-base font-medium cursor-pointer focus:outline-none focus:ring-2 focus:ring-primary ${errors.countryCode ? 'border-red-500' : 'border-gray-200 dark:border-gray-700'}`}
                    data-testid="campaign-country-select"
                    required
                  >
                    {COUNTRY_CODES.map((country, index) => (
                      country.code === 'DIVIDER' ? (
                        <option key={`divider-${index}`} disabled value="DIVIDER">
                          ──────────────────────────
                        </option>
                      ) : (
                        <option key={country.code} value={country.code}>
                          {country.dialCode} {country.name}
                        </option>
                      )
                    ))}
                  </select>
                  <ChevronDown className="absolute right-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400 pointer-events-none" />
                </div>
                {errors.countryCode && <p className="text-red-500 text-xs">{errors.countryCode}</p>}

                {/* Phone Number Input - Separate field below dropdown */}
                <div>
                  <Input
                    type="tel"
                    placeholder="Enter your phone number"
                    value={formData.phone}
                    onChange={handlePhoneChange}
                    className={`w-full h-12 rounded-xl border-gray-200 dark:border-gray-700 dark:bg-gray-800 dark:text-white focus:border-gray-400 text-base ${errors.phone ? 'border-red-500' : ''}`}
                    data-testid="campaign-phone-input"
                    required
                  />
                </div>
                
                {errors.phone && <p className="text-red-500 text-xs mt-1">{errors.phone}</p>}
                
                {/* Dynamic helper text showing required digits */}
                <div className="bg-gray-100 dark:bg-gray-800 rounded-lg px-3 py-2">
                  <p className="text-gray-600 dark:text-gray-300 text-sm">
                    <span className="font-medium">{selectedCountry.name}:</span>{' '}
                    {selectedCountry.minLength === selectedCountry.maxLength 
                      ? <span>Enter exactly <span className="font-bold text-primary">{selectedCountry.minLength}</span> digits</span>
                      : <span>Enter <span className="font-bold text-primary">{selectedCountry.minLength}-{selectedCountry.maxLength}</span> digits</span>
                    }
                    <span className="text-gray-400 dark:text-gray-500 block text-xs mt-1">Don't include country code - it's selected above</span>
                  </p>
                </div>
              </div>

              <div className="space-y-2">
                <Label className="text-gray-700 dark:text-gray-200 flex items-center gap-2">
                  <Mail className="w-4 h-4" />
                  Email Address <span className="text-red-500">*</span>
                </Label>
                <Input
                  type="email"
                  placeholder="your@email.com"
                  value={formData.email}
                  onChange={(e) => {
                    setFormData(prev => ({ ...prev, email: e.target.value }));
                    if (errors.email) setErrors(prev => ({ ...prev, email: '' }));
                  }}
                  className={`rounded-xl border-gray-200 dark:border-gray-700 dark:bg-gray-800 dark:text-white focus:border-gray-400 ${errors.email ? 'border-red-500' : ''}`}
                  data-testid="campaign-email-input"
                  required
                />
                {errors.email && <p className="text-red-500 text-xs">{errors.email}</p>}
              </div>

              <Button
                type="submit"
                disabled={submitting}
                className={`w-full rounded-full py-6 text-lg font-medium ${theme.buttonBg} text-white`}
                data-testid="campaign-submit-btn"
              >
                {submitting ? 'Joining...' : 'Join Founders List'}
              </Button>
            </form>

            <p className="text-center text-gray-400 dark:text-gray-500 text-xs mt-6">
              By registering, you agree to receive promotional messages from Bulaccino Cafe.
            </p>
          </motion.div>

          {/* Footer */}
          <motion.p
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.4 }}
            className={`text-center ${theme.footerColor} mt-8 font-medium`}
          >
            {campaign?.footer}
          </motion.p>
        </div>
      </main>
    </div>
  );
}
